/*
 * Encart de retour visiteur (snippets/catalogue-feedback.liquid).
 *
 * Parcours en deux temps, chacun envoyé à une application web Google Apps
 * Script qui écrit dans une Google Sheet (docs/FEEDBACK-catalogue.md) :
 *   1. clic sur Oui / Non / Pas sûr  → etape « reponse »  (l'essentiel)
 *   2. commentaire et/ou e-mail      → etape « details »  (facultatif)
 * Les deux envois portent le même `id` : le script met à jour la même ligne et
 * n'écrase jamais un champ rempli par du vide. Chaque envoi contient aussi tout
 * le contexte, si bien que l'étape 2 suffit à créer la ligne complète même si
 * l'étape 1 a échoué.
 *
 * La réponse du script est lue : le remerciement ne s'affiche que si la ligne
 * est bien enregistrée, sinon le visiteur voit un message d'erreur et peut
 * réessayer. Les échecs sont aussi tracés dans la console ([Greez feedback]).
 *
 * Événements Intelligems (la réponse fermée y est mesurée sans aucun stockage) :
 *   feedback_encart_affiche      encart visible à au moins 50 %
 *   feedback_reponse_oui / _non / _pas_sur
 *   feedback_commentaire_envoye  étape 2 envoyée
 *
 * ⚠️ Fichier IDENTIQUE sur les deux thèmes du test. L'adresse d'envoi est une
 * constante, et non un réglage de thème, pour qu'elle ne puisse pas diverger.
 */
(() => {
  // ─── À ajuster ─────────────────────────────────────────────
  // URL de l'application web Apps Script (se termine par /exec).
  // Vide = encart masqué : rien n'est proposé tant que l'envoi n'est pas branché.
  const ENDPOINT = 'https://script.google.com/macros/s/AKfycby7XGDzXcV59fpzDvi-rMQYS8pCESg1Hqt-5-s4hs4tQwnEDD__LLk5M70HgjAy5knz/exec';
  const MASQUER_APRES_REPONSE_JOURS = 30; // ne plus reproposer l'encart
  // ID de l'expérience de thème Intelligems. Vide = premier test auquel le
  // visiteur est affecté (à renseigner si plusieurs tests tournent en parallèle).
  const EXPERIENCE_THEME_ID = '';
  // ───────────────────────────────────────────────────────────

  const CLE_STOCKAGE = 'greez_catalogue_feedback';

  const racine = document.querySelector('[data-catalogue-feedback]');
  if (!racine || !ENDPOINT) return;

  const pousser = (nom) => {
    window.igEvents = window.igEvents || [];
    window.igEvents.push({ event: nom });
  };

  const aRepondu = () => {
    try {
      const stocke = JSON.parse(window.localStorage.getItem(CLE_STOCKAGE) || 'null');
      if (!stocke || !stocke.reponduLe) return false;
      return Date.now() - stocke.reponduLe < MASQUER_APRES_REPONSE_JOURS * 24 * 60 * 60 * 1000;
    } catch (erreur) {
      return false;
    }
  };

  const memoriserReponse = () => {
    try {
      window.localStorage.setItem(CLE_STOCKAGE, JSON.stringify({ reponduLe: Date.now() }));
    } catch (erreur) {
      // Stockage indisponible (navigation privée stricte) : l'encart sera reproposé.
    }
  };

  if (aRepondu()) return;
  racine.hidden = false;

  // ─── Impression : encart visible à au moins 50 % ───────────
  if ('IntersectionObserver' in window) {
    const observateur = new IntersectionObserver((entrees) => {
      if (entrees.some((entree) => entree.isIntersecting)) {
        pousser('feedback_encart_affiche');
        observateur.disconnect();
      }
    }, { threshold: 0.5 });
    observateur.observe(racine);
  } else {
    pousser('feedback_encart_affiche');
  }

  // ─── Contexte joint à chaque envoi ─────────────────────────
  const genererId = () => {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (caractere) => {
      const aleatoire = (Math.random() * 16) | 0;
      return (caractere === 'x' ? aleatoire : (aleatoire & 0x3) | 0x8).toString(16);
    });
  };

  const tronquer = (valeur, max) => String(valeur || '').slice(0, max);

  const lireGroupeIntelligems = () => {
    const utilisateur = window.igData && window.igData.user;
    if (!utilisateur || typeof utilisateur.getTestGroup !== 'function') return {};
    try {
      const experiences = EXPERIENCE_THEME_ID
        ? [{ id: EXPERIENCE_THEME_ID, name: '' }]
        : (typeof utilisateur.getExperiments === 'function' ? utilisateur.getExperiments() : []) || [];
      for (const experience of experiences) {
        const groupe = utilisateur.getTestGroup(experience.id);
        if (groupe) {
          return {
            intelligems_experience: tronquer(experience.name || experience.id, 200),
            intelligems_groupe: tronquer(groupe.name, 200),
            intelligems_groupe_id: tronquer(groupe.id, 100),
            intelligems_controle: Boolean(groupe.isControl),
          };
        }
      }
    } catch (erreur) {
      return {};
    }
    return {};
  };

  const contexte = () => {
    const parametres = new URLSearchParams(window.location.search);
    const theme = (window.Shopify && window.Shopify.theme) || {};
    return {
      page_url: tronquer(window.location.pathname + window.location.search, 500),
      page_type: tronquer(racine.dataset.pageType, 50),
      collection_handle: tronquer(racine.dataset.collectionHandle, 200),
      recherche: tronquer(parametres.get('q'), 200),
      appareil: window.matchMedia('(max-width: 767px)').matches ? 'mobile' : 'desktop',
      langue: tronquer(document.documentElement.lang, 20),
      theme_ab_label: tronquer(window.greezAbThemeConfig && window.greezAbThemeConfig.themeLabel, 20),
      theme_id: tronquer(theme.id, 50),
      theme_nom: tronquer(theme.name, 200),
      ...lireGroupeIntelligems(),
    };
  };

  // Requête « simple » (text/plain, sans en-tête personnalisé) : pas de
  // pré-vérification CORS, et Apps Script autorise la lecture de sa réponse.
  const envoyer = (donnees, keepalive) => window
    .fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify(donnees),
      keepalive,
    })
    .then((reponse) => reponse.json())
    .then((resultat) => {
      if (resultat && resultat.ok === true) return true;
      window.console && window.console.warn('[Greez feedback] envoi refusé', resultat);
      return false;
    })
    .catch((erreur) => {
      window.console && window.console.warn('[Greez feedback] envoi impossible', erreur);
      return false;
    });

  // ─── Éléments ──────────────────────────────────────────────
  const id = genererId();
  const choix = Array.from(racine.querySelectorAll('[data-feedback-choice]'));
  const formulaire = racine.querySelector('[data-feedback-form]');
  const champCommentaire = formulaire.querySelector('[name="commentaire"]');
  const champEmail = formulaire.querySelector('[name="email"]');
  const boutonEnvoyer = formulaire.querySelector('[type="submit"]');
  const zoneErreur = racine.querySelector('[data-feedback-error]');
  const remerciement = racine.querySelector('[data-feedback-thanks]');

  let reponseChoisie = '';

  const afficherErreur = (message) => {
    zoneErreur.textContent = message;
    zoneErreur.hidden = false;
  };

  const terminer = () => {
    formulaire.hidden = true;
    remerciement.hidden = false;
  };

  // ─── Étape 1 : la question fermée ──────────────────────────
  choix.forEach((bouton) => {
    bouton.addEventListener('click', () => {
      if (reponseChoisie) return;
      reponseChoisie = bouton.dataset.feedbackChoice;

      choix.forEach((autre) => {
        autre.setAttribute('aria-pressed', String(autre === bouton));
        autre.disabled = true;
      });

      pousser(`feedback_reponse_${reponseChoisie}`);
      memoriserReponse();
      formulaire.hidden = false;

      // keepalive : le visiteur peut quitter la page juste après son clic.
      envoyer({ id, etape: 'reponse', reponse: reponseChoisie, ...contexte() }, true);
    });
  });

  // ─── Étape 2 : commentaire et e-mail facultatifs ───────────
  formulaire.addEventListener('submit', async (evenement) => {
    evenement.preventDefault();
    zoneErreur.hidden = true;

    const commentaire = champCommentaire.value.trim();
    const email = champEmail.value.trim();

    if (!commentaire && !email) {
      terminer();
      return;
    }

    if (email && !champEmail.checkValidity()) {
      afficherErreur(racine.dataset.emailInvalidMessage);
      champEmail.focus();
      return;
    }

    boutonEnvoyer.disabled = true;
    const envoye = await envoyer({
      id,
      etape: 'details',
      reponse: reponseChoisie,
      commentaire: tronquer(commentaire, 1000),
      email: tronquer(email, 254),
      ...contexte(),
    }, false);

    if (envoye) {
      pousser('feedback_commentaire_envoye');
      terminer();
    } else {
      boutonEnvoyer.disabled = false;
      afficherErreur(racine.dataset.errorMessage);
    }
  });
})();
